import { UserLevel } from '@/types/auth.types';
import { PatientGender } from '@/types/patient.types';
import { HealthMarkerType } from '@/types/healthMarker.types';

// API Configuration
export const API_CONFIG = {
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8080',
  timeout: 30000,
  retries: 3,
} as const;

// User Level Constants
export const USER_LEVELS = {
  [UserLevel.MATERNITY]: 'Maternitate',
  [UserLevel.HOSPITAL]: 'Spital', 
  [UserLevel.ADMINISTRATOR]: 'Administrator',
} as const;

// Gender Constants
export const GENDER_OPTIONS = {
  [PatientGender.FEMALE]: 'Feminin',
  [PatientGender.MALE]: 'Masculin',
} as const;

// Health Marker Types
export const HEALTH_MARKER_TYPES = {
  [HealthMarkerType.HEART_RATE]: {
    name: 'Frecvența Cardiacă',
    unit: 'bpm',
    isNumeric: true,
  },
  [HealthMarkerType.BLOOD_PRESSURE]: {
    name: 'Tensiune Arterială',
    unit: 'mmHg',
    isNumeric: true,
  },
  [HealthMarkerType.OXYGEN_SATURATION]: {
    name: 'Saturația Oxigenului',
    unit: '%',
    isNumeric: true,
  },
  [HealthMarkerType.WEIGHT]: {
    name: 'Greutate',
    unit: 'kg',
    isNumeric: true,
  },
  [HealthMarkerType.HEIGHT]: {
    name: 'Înălțime',
    unit: 'cm',
    isNumeric: true,
  },
  [HealthMarkerType.TEMPERATURE]: {
    name: 'Temperatură',
    unit: '°C',
    isNumeric: true,
  },
  [HealthMarkerType.MEDICATION]: {
    name: 'Medicație',
    unit: '',
    isNumeric: false,
  },
  [HealthMarkerType.PROCEDURE]: {
    name: 'Procedură',
    unit: '',
    isNumeric: false,
  },
} as const;


// Severity Levels
export const SEVERITY_LEVELS = {
  1: { label: 'Ușoară', color: 'text-green-600', bgColor: 'bg-green-100' },
  2: { label: 'Moderată', color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
  3: { label: 'Severă', color: 'text-orange-600', bgColor: 'bg-orange-100' },
  4: { label: 'Critică', color: 'text-red-600', bgColor: 'bg-red-100' },
  5: { label: 'Foarte critică', color: 'text-red-800', bgColor: 'bg-red-200' },
} as const;

// Validation Constants
export const VALIDATION_RULES = {
  name: { minLength: 2, maxLength: 100 },
  birthWeight: { min: 300, max: 6000 }, // grams
  gestationalAge: { min: 22, max: 43 }, // weeks
  maternalAge: { min: 10, max: 60 }, // years
  password: { minLength: 8 },
  notes: { maxLength: 500 },
  description: { maxLength: 500 },
} as const;

// Pagination Constants
export const PAGINATION = {
  defaultPageSize: 20,
  pageSizeOptions: [10, 20, 50, 100],
  maxPageSize: 100,
} as const;

// Date Formats
export const DATE_FORMATS = {
  display: 'DD.MM.YYYY',
  input: 'YYYY-MM-DD',
  api: 'YYYY-MM-DD',
  datetime: 'DD.MM.YYYY HH:mm',
} as const;

// Chart Colors
export const CHART_COLORS = {
  primary: '#3b82f6',
  secondary: '#10b981',
  accent: '#f59e0b',
  danger: '#ef4444',
  warning: '#f59e0b',
  info: '#3b82f6',
  success: '#10b981',
  muted: '#6b7280',
} as const;

// File Upload Constants
export const FILE_UPLOAD = {
  maxSize: 10 * 1024 * 1024, // 10MB
  allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
  allowedExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.pdf'],
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  accessToken: 'access_token',
  refreshToken: 'refresh_token',
  tokenExpiry: 'token_expiry',
  currentUser: 'current_user',
  userPreferences: 'user_preferences',
  lastRoute: 'last_route',
} as const;

// Error Codes
export const ERROR_CODES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

// Navigation Routes (without React Router)
export const ROUTES = {
  login: '/login',
  dashboard: '/dashboard',
  patients: '/patients',
  patientDetails: (id: string) => `/patients/${id}`,
  diagnoses: '/diagnoses',
  healthMarkers: '/health-markers',
  maternalData: '/maternal-data',
  users: '/users',
  reports: '/reports',
  profile: '/profile',
  settings: '/settings',
} as const;